/** biome-ignore-all lint/correctness/useHookAtTopLevel: <explanation> */
import React from 'react';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles } from 'rewire-ui';
import Badge from '@material-ui/core/Badge';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { IEmployeeSummary } from '../DataTypes';
import { makeStyles } from '../utility/styles';
import RoutedEntityListCardView, { IRoutedEntityListCardViewProps, IItemRendererProps } from '../ui/RoutedEntityListCardView';
import { SummaryListModel } from './SummaryListModel';

const itemRendererStyles = (theme: Theme) => ({
  name: {
    color: theme.palette.dialogPrimary.main,
    fontWeight: 'bold',
  },
  badges: {
    flex: '0 0 auto',
  },
  badge: {
    margin: '2px 11px',
    '&.info>span': {
      backgroundColor: theme.palette.info.main,
    },
    lineHeight: '2px',
    '&>span': {
      color: 'white',
    },
  },
  saving: {
    backgroundColor: theme.palette.success.main,
    padding: '2px 5px',
    borderRadius: '4px',
    fontSize: '0.7rem',
    color: 'white',
    display: 'flex',
    margin: '0px -4px 0px 2px',
    cursor: 'wait',
  },
});

const useItemRendererStyles = makeStyles(itemRendererStyles);

export const EmployeeItemRenderer = ({ entity, classes }: IItemRendererProps) => (
  <Observe
    render={() => {
      classes = useItemRendererStyles({ classes });
      const employee = entity as IEmployeeSummary;
      const saving = employee.saving;
      const warnings = employee.warning > 0 && (
        <Badge className={classes.badge} color="secondary" badgeContent={employee.warning}>
          &nbsp;
        </Badge>
      );
      const errors = employee.error > 0 && (
        <Badge className={classes.badge} color="error" badgeContent={employee.error}>
          &nbsp;
        </Badge>
      );
      const info = employee.info > 0 && (
        <Badge className={classes.badge + ' info'} badgeContent={employee.info}>
          &nbsp;
        </Badge>
      );
      return (
        <>
          {saving && <span className={classes.saving}>Saving</span>}
          <Typography variant="body2" className={classes.name} display="inline">
            {employee.name}
          </Typography>
          <span className={classes.badges}>
            {info}
            {warnings}
            {errors}
          </span>
        </>
      );
    }}
  />
);

const styles = (_theme: Theme) => ({
  savingCursor: {
    cursor: 'wait',
  },
});

export type SummaryListCardStyles = ReturnType<typeof styles>;

interface ISummaryListCardProps extends Omit<IRoutedEntityListCardViewProps, 'model' | 'entity'> {
  model: SummaryListModel;
  entity: IEmployeeSummary;
}

type SummaryListCardProps = WithStyle<SummaryListCardStyles, ISummaryListCardProps>;

export class SummaryListCardView extends React.Component<SummaryListCardProps> {
  constructor(props: SummaryListCardProps) {
    super(props);
  }

  render() {
    const { classes, entity, model, itemRenderer, itemClasses } = this.props;
    const saving = entity.saving;
    const ItemRenderer = itemRenderer || EmployeeItemRenderer;
    const { savingCursor, ...restClasses } = classes;

    return (
      <RoutedEntityListCardView
        model={model}
        onClick={() => () => {}}
        entity={entity}
        classes={{ link: saving ? savingCursor : undefined, ...restClasses }}
        itemRenderer={ItemRenderer}
        itemClasses={itemClasses}
      />
    );
  }
}

export default withStyles(styles, SummaryListCardView);
